import { useMemo } from 'react';
import { SideDrawer } from '@/components/drawer/SideDrawer';
import { DrawerSection } from '@/components/drawer/DrawerSection';
import { InfoMessage } from '@/components/drawer/InfoMessage';
import { useLoanQuery, usePolicyLoansQuery } from '@/api/query/loan';
import { useRefreshPolicyData } from '@/api/query/refresh';
import type { Member, Policy } from '@/types';
import { formatMonthKey } from '@/lib/months';
import { LOAN_MIN_PAID_MONTHS } from '@/lib/loans';
import { LoanStatusCard } from './LoanStatusCard';
import { NewLoanForm } from './NewLoanForm';
import { LoanPaymentForm } from './LoanPaymentForm';
import { memberHeading } from '@/lib/utils';
import { LoanHistory } from './LoanHistory';

interface PolicyLoanProps {
  member: Member | null;
  policy: Policy | null;
  /** The member's balance due, shown when a repayment is Paid by PMS. */
  balanceDue: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Policy Loan drawer. Every loan state uses the same three blocks:
 * 1. Loan Status, 2. the action for that state, 3. History.
 */
export function PolicyLoan({ member, policy, balanceDue, open, onOpenChange }: PolicyLoanProps) {
  const refreshPolicyData = useRefreshPolicyData();
  const { data: policyLoans } = usePolicyLoansQuery(policy?.id, open);
  const { data: activeLoan } = useLoanQuery(policy?.loan.loanId, open);

  const closedLoans = useMemo(
    () => (policyLoans ?? []).filter((loan) => loan.status === 'closed'),
    [policyLoans]
  );

  const handleSaved = async () => {
    await refreshPolicyData();
    onOpenChange(false);
  };

  const renderAction = (current: Policy) => {
    switch (current.loan.eligibility) {
      case 'active':
        return activeLoan ? (
          <LoanPaymentForm
            key={activeLoan.id}
            loan={activeLoan}
            memberName={member?.name ?? ''}
            balanceDue={balanceDue}
            onSaved={handleSaved}
          />
        ) : null;
      case 'eligible':
        return <NewLoanForm key={current.id} policy={current} onSaved={handleSaved} />;
      case 'waiting':
        return (
          <DrawerSection>
            <InfoMessage>
              A new loan can be given from <b>{formatMonthKey(current.loan.availableFrom)}</b>, the month after the
              previous loan closed.
            </InfoMessage>
          </DrawerSection>
        );
      case 'policy_closed':
        return (
          <DrawerSection>
            <InfoMessage>This policy is closed. Loans are not available.</InfoMessage>
          </DrawerSection>
        );
      default: {
        const needed = current.loan.monthsNeeded ?? LOAN_MIN_PAID_MONTHS - current.paidMonths;
        return (
          <DrawerSection>
            <InfoMessage>
              This policy needs <b>{needed} more policy {needed === 1 ? 'payment' : 'payments'}</b> before a loan can be
              given.
            </InfoMessage>
          </DrawerSection>
        );
      }
    }
  };

  return (
    <SideDrawer open={open} onOpenChange={onOpenChange} title="Policy Loan" member={member} tag={policy?.policyNo}>
      {policy && (
        <>
          <LoanStatusCard policy={policy} activeLoan={activeLoan} closedLoans={closedLoans} />
          {renderAction(policy)}
          <LoanHistory
            key={policy.id}
            subtitle={member ? `${memberHeading(member)} · ${policy.policyNo}` : policy.policyNo}
            activeLoan={activeLoan}
            closedLoans={closedLoans}
          />
        </>
      )}
    </SideDrawer>
  );
}
